/**
 * Во что превращается копия перед тем, как покинуть устройство.
 *
 * Единственное место, где принимается решение «шифровать или нет». Пути наружу
 * четыре — скачивание, файл на телефоне, папка облака и Яндекс.Диск, — и если
 * бы каждый решал сам, однажды один из них остался бы незашифрованным. Такую
 * дыру не видно ни в интерфейсе, ни в тестах соседних путей: человек включил
 * пароль, а копия всё это время уезжала открытой.
 */

import {
  decryptBackup,
  type EncryptedBackup,
  encryptBackup,
  isEncryptedBackup,
} from './backup-crypto';
import { readBackupPassword } from './backup-password';
import type { Backup } from './export-data';

/** Текст файла копии: зашифрованный, если человек включил пароль. */
export async function serializeBackup(userId: string, backup: Backup): Promise<string> {
  const password = await readBackupPassword(userId);
  if (password === null) return JSON.stringify(backup, null, 2);

  return JSON.stringify(await encryptBackup(backup, password));
}

export type ParsedPayload =
  | { kind: 'plain'; text: string }
  | { kind: 'encrypted'; file: EncryptedBackup };

/**
 * Разбирает файл настолько, чтобы понять, нужен ли пароль.
 *
 * Дальше расходятся дороги: обычная копия идёт в разбор как раньше, а
 * зашифрованную сначала показывают человеку с просьбой ввести пароль.
 */
export function parseBackupPayload(text: string): ParsedPayload {
  try {
    const raw: unknown = JSON.parse(text);
    if (isEncryptedBackup(raw)) return { kind: 'encrypted', file: raw };
  } catch {
    // Не JSON вовсе — пусть об этом скажет обычный разбор, у него для этого
    // есть человеческие слова.
  }

  return { kind: 'plain', text };
}

/** Расшифровывает копию и отдаёт её текстом — дальше обычный разбор. */
export async function openEncrypted(file: EncryptedBackup, password: string): Promise<string> {
  return JSON.stringify(await decryptBackup(file, password));
}
